import React, { useEffect, useMemo, useState } from 'react';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  isBefore,
  isAfter,
} from 'date-fns';
import { IoIosArrowBack, IoIosArrowForward } from 'react-icons/io';
import { getCurrentDay } from 'src/app/utils/calendar.utils';

type CalendarType = 'light' | 'full';

type CalendarProps = {
  selectedDate?: Date;
  showCurrentDate?: boolean;
  onDateSelect: (day: Date) => void;
  maxDate?: Date;
  minDate?: Date;
  initialDay?: Date;
  className?: string;
  vyduraEveryDayDates?: Date[];
  vyduraPerAttacksDates?: Date[];
  migraineDates?: Date[];
  datesDisabled?: Date[];
  type?: CalendarType;
  onMonthChange?: (month: string) => void;
  id?: string;
  size?: 'small' | 'normal';
};

const Calendar = ({
  selectedDate,
  onDateSelect,
  minDate,
  maxDate,
  className,
  initialDay,
  showCurrentDate = true,
  vyduraEveryDayDates = [],
  vyduraPerAttacksDates = [],
  migraineDates = [],
  datesDisabled = [],
  onMonthChange,
  type = 'full',
  id,
  size = 'normal',
}: CalendarProps) => {
  const [initialDate, setInitialDate] = useState(
    initialDay ?? selectedDate ?? new Date()
  );
  const startDate = startOfWeek(startOfMonth(initialDate));
  const endDate = endOfWeek(endOfMonth(initialDate));
  const isShowNextDisabled = useMemo(
    () => maxDate && !isAfter(maxDate, endOfMonth(initialDate)),
    [initialDate, maxDate]
  );
  const isShowPrevDisabled = useMemo(
    () => minDate && !isBefore(minDate, startOfMonth(initialDate)),
    [initialDate, minDate]
  );

  const days = eachDayOfInterval({ start: startDate, end: endDate });
  const isDateInList = (date: Date, dateList: Date[]) => {
    return dateList.some((d) => date.toDateString() === d.toDateString());
  };
  const handleShowNextMonth = () => {
    setInitialDate(
      (prev) =>
        new Date(prev.getFullYear(), prev.getMonth() + 1, prev.getDate())
    );
  };
  const handleShowPrevMonth = () => {
    setInitialDate(
      (prev) =>
        new Date(prev.getFullYear(), prev.getMonth() - 1, prev.getDate())
    );
  };
  const medicationClassNames = `absolute inline-block border border-transparent rounded-full ${size === 'small' ? 'w-[3px] h-[3px]' : 'w-[6px] h-[6px]'}`;

  useEffect(() => {
    if (initialDate && onMonthChange) {
      onMonthChange(getCurrentDay(initialDate).month);
    }
  }, [initialDate, onMonthChange]);

  return (
    <div
      id={id ?? ''}
      className={`w-full ${size === 'normal' ? 'p-[16px]' : 'p-[10px]'} ${type === 'full' ? 'bg-grey rounded-[8px]' : 'bg-white'} ${className}`}
    >
      <div
        className={`text-center ${size === 'normal' ? 'text-[18px]' : 'text-[12px]'} my-[10px] flex items-center ${type === 'full' ? 'justify-between' : 'justify-center'}`}
      >
        {type === 'full' && (
          <IoIosArrowBack
            color={isShowPrevDisabled ? 'grey' : 'black'}
            onClick={isShowPrevDisabled ? () => undefined : handleShowPrevMonth}
            className='cursor-pointer'
          />
        )}
        <span
          className={
            type === 'light' && isSameMonth(initialDate, new Date())
              ? 'font-bold'
              : ''
          }
        >
          {type === 'full'
            ? format(initialDate, 'MMMM yyyy')
            : format(initialDate, 'MMMM')}
        </span>
        {type === 'full' && (
          <IoIosArrowForward
            color={isShowNextDisabled ? 'grey' : 'black'}
            onClick={isShowNextDisabled ? () => undefined : handleShowNextMonth}
            className='cursor-pointer'
          />
        )}
      </div>
      {type === 'full' && (
        <div className='grid grid-cols-7 text-center text-text-gray my-[30px]'>
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <div key={day} className='text-[13px]'>
              {day}
            </div>
          ))}
        </div>
      )}
      <div className='grid grid-cols-7 gap-2'>
        {days.map((day, index) => {
          const isOutOfRange =
            (minDate ? isBefore(day, minDate) : false) ||
            (maxDate ? isAfter(day, maxDate) : false);
          const isSelected = selectedDate && isSameDay(day, selectedDate);
          const isCurrentDay = isSameDay(day, new Date());
          const isOutOfMonth = !isSameMonth(day, initialDate);
          const isMigraineDay = isDateInList(day, migraineDates);
          const outOfMonthClassName =
            type === 'full'
              ? isSelected
                ? 'opacity-100'
                : 'opacity-40'
              : 'opacity-0';
          const selectedDateClassname =
            type === 'light' || migraineDates.length
              ? isMigraineDay
                ? `border border-[var(--color-darken)] ${size === 'normal' ? 'border-[4px]' : 'border-[2px]'}`
                : 'bg-[var(--color-darken)]'
              : 'bg-dark-grey text-white opacity-100';
          return (
            <div
              key={index}
              className={`flex justify-center items-center rounded-full font-semibold relative cursor-pointer bg-clip-padding 
                    ${size === 'normal' ? 'text-[16px]' : 'text-[9px]'}
                    ${size === 'normal' ? 'w-10 h-10' : 'w-4 h-4'}
                    ${isOutOfMonth ? outOfMonthClassName : 'opacity-100'}
                    ${isCurrentDay && showCurrentDate && 'bg-[var(--color-darken)]'}
                    ${isSelected && selectedDateClassname}
                    ${(isOutOfRange && !isOutOfMonth) || isDateInList(day, datesDisabled) ? 'opacity-20 pointer-events-none' : ''}
                    ${isMigraineDay ? 'bg-red text-white' : ''}
                  `}
              onClick={() =>
                isDateInList(day, datesDisabled) ? undefined : onDateSelect(day)
              }
            >
              <span className={size === 'small' ? 'leading-[16px]' : ''}>
                {day.getDate()}
              </span>
              {isDateInList(day, vyduraEveryDayDates) && (
                <span
                  className={`${medicationClassNames} bg-primary`}
                  style={{
                    left:
                      size === 'small'
                        ? 'calc(50% - 1.5px)'
                        : 'calc(50% - 3px)',
                    bottom:
                      size === 'small'
                        ? isSelected && isMigraineDay
                          ? '-7px'
                          : '-6px'
                        : isSelected && isMigraineDay
                          ? '-12px'
                          : '-7px',
                  }}
                />
              )}
              {isDateInList(day, vyduraPerAttacksDates) && (
                <span
                  className={`${medicationClassNames} bg-black`}
                  style={{
                    left:
                      size === 'small'
                        ? 'calc(50% - 1.5px)'
                        : 'calc(50% - 3px)',
                    bottom:
                      size === 'small'
                        ? '-6px'
                        : isSelected && isMigraineDay
                          ? '-12px'
                          : '-9px',
                  }}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Calendar;
